import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { DeviceCreate, DeviceUpdate } from '../../api/models';
import { ManagementOauthApi } from '../../api/apis';
import { apiClient } from '../../services/apiClient';
import { deviceService, DeviceDetail, DeviceFirmwareVersion, TemplateForDevice } from '../../services/deviceService';
import { typesService, HardwareType, ModelReference, HardwareFieldDefinition } from '../../services/typesService';
import { clientService } from '../../services/clientService';
import { Client } from '../../types/client';
import { CustomField } from '../../types/configTemplate';
import { FieldGroupSection, FieldGrid } from '../common/FieldGroupSection';
import { AdditionalFieldsEditor } from '../common/AdditionalFieldsEditor';
import SearchableSelect from '../common/SearchableSelect';
import { categorizeDeviceFieldsFromSchema, FieldGroupData, getEditableFields, buildOverrides } from '../../utils/fieldGrouping';
import ConfigSourcesPanel from './ConfigSourcesPanel';
import ConfigChainPanel from './ConfigChainPanel';
import AuditHistoryTab from '../audit/AuditHistoryTab';
import { useClientAccess } from '../../hooks/useClientAccess';
import { useRawTabVisible } from '../../hooks/useRawTabVisible';
import { useI18n } from '../../contexts/I18nContext';
import './DeviceDetailModal.css';

interface DeviceDetailModalProps {
  device: DeviceCreate | null;
  isOpen: boolean;
  onClose: () => void;
  onDeviceUpdated?: (device: DeviceCreate) => void;
}

export const DeviceDetailModal: React.FC<DeviceDetailModalProps> = ({
  device,
  isOpen,
  onClose,
  onDeviceUpdated
}) => {
  // Tab state
  const [activeTab, setActiveTab] = useState<'basic' | 'settings' | 'raw' | 'v2conf' | 'history'>('basic');
  const { canViewAuditLog } = useClientAccess();
  const rawTabVisible = useRawTabVisible();
  const { formatDateTime, t } = useI18n();

  // If the admin hid the RAW tab while the user was on it, fall back to
  // the default tab so the dialog isn't stuck on an invisible panel.
  useEffect(() => {
    if (rawTabVisible === false && activeTab === 'raw') {
      setActiveTab('basic');
    }
  }, [rawTabVisible, activeTab]);

  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Device detail state
  const [deviceDetail, setDeviceDetail] = useState<DeviceDetail | null>(null);

  // Raw tab state
  const [rawCopied, setRawCopied] = useState(false);
  const [rawRefreshing, setRawRefreshing] = useState(false);

  // V2/CONF tab state
  const [v2ConfData, setV2ConfData] = useState<Record<string, unknown> | null>(null);
  const [v2ConfLoading, setV2ConfLoading] = useState(false);
  const [v2ConfCopied, setV2ConfCopied] = useState(false);
  // Strict mode toggle for the V2/CONF tab — when true, the backend
  // returns only fields declared on the hardware type's schema + identity
  // metadata (matches what the Settings tab shows). Opt-in via
  // ?strict=true query parameter so legacy clients see no change.
  const [v2ConfStrict, setV2ConfStrict] = useState(false);

  // Section 1: Device Identity
  const [hardwareTypes, setHardwareTypes] = useState<HardwareType[]>([]);
  const [modelRefs, setModelRefs] = useState<ModelReference[]>([]);
  const [selectedTypeCode, setSelectedTypeCode] = useState<string>('');
  const [selectedModelCode, setSelectedModelCode] = useState<string>('');
  const [clientName, setClientName] = useState<string>('');
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<string>('');
  const [editedPhone, setEditedPhone] = useState<string>('');

  // Section 2: Firmware
  const [firmwareVersions, setFirmwareVersions] = useState<DeviceFirmwareVersion[]>([]);
  const [firmwareLoading, setFirmwareLoading] = useState(false);

  // Section 3: Configuration Template
  const [templates, setTemplates] = useState<TemplateForDevice[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
  const [maxTemplatePriority, setMaxTemplatePriority] = useState<string>('');
  const [fieldDefinitions, setFieldDefinitions] = useState<HardwareFieldDefinition[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [resetting, setResetting] = useState(false);

  // Settings tab edit mode
  const [isEditingSettings, setIsEditingSettings] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [customFields, setCustomFields] = useState<CustomField[]>([]);

  // Dynamic field grouping state
  const [expandedGroups, setExpandedGroups] = useState<string[]>(['mqtt', 'syslog', 'provisioning', 'general', 'additional']);
  const [categorizedFields, setCategorizedFields] = useState<FieldGroupData[]>([]);
  const [settingsForm, setSettingsForm] = useState<Record<string, any>>({});

  // Legacy device column editing: users can opt in to see/edit raw
  // device columns (syslog_host, mqtt_host, etc.) that are NOT declared
  // on the hardware type's schema. These edit the device row directly
  // via PATCH /v2/conf/{id}, NOT config_override_json. The toggle is
  // only visible while isEditingSettings is true.
  const [showLegacyColumns, setShowLegacyColumns] = useState(false);
  const [legacyColumnForm, setLegacyColumnForm] = useState<Record<string, any>>({});

  const managementApi = useMemo(
    () => new ManagementOauthApi(apiClient.getConfiguration()),
    [],
  );

  // Load hardware types and clients on mount.
  // Active-only — even in edit mode we don't offer soft-deleted types
  // as picks. If the device's current type is inactive the value stays
  // (handled by the render layer below) but the dropdown won't list it
  // as a switch-to option. The render also defends via
  // .filter(t => t.is_active) as belt-and-suspenders.
  useEffect(() => {
    typesService.getHardwareTypes(false).then(setHardwareTypes);
    clientService.fetchAllClients().then(setClients).catch(console.error);
  }, []);

  // Load device detail and firmware when modal opens
  useEffect(() => {
    if (isOpen && device?.idDevice) {
      // Reset states
      setActiveTab('basic');
      setIsEditing(false);
      setError(null);
      setV2ConfData(null);
      setV2ConfLoading(false);
      setV2ConfStrict(false);

      // Load device detail
      deviceService.getDeviceDetail(device.idDevice)
        .then(detail => {
          setDeviceDetail(detail);
          setSelectedTemplateId(detail.template_id);
          setMaxTemplatePriority(detail.max_template_priority != null ? String(detail.max_template_priority) : '');
        })
        .catch(err => console.error('Failed to load device detail:', err));

      // Load firmware versions
      setFirmwareLoading(true);
      deviceService.getDeviceFirmwareVersions(device.idDevice)
        .then(setFirmwareVersions)
        .catch(err => {
          console.error('Failed to load firmware versions:', err);
          setFirmwareVersions([]);
        })
        .finally(() => setFirmwareLoading(false));

      // Load available templates
      setTemplatesLoading(true);
      deviceService.getTemplatesForDevice(device.idDevice)
        .then(setTemplates)
        .catch(err => console.error('Failed to load templates:', err))
        .finally(() => setTemplatesLoading(false));

      // Initialize edit state from device
      // Note: model and model_ref may come from API but aren't in the strict interface
      setSelectedTypeCode(device.deviceType || '');
      const deviceAny = device as any;
      setSelectedModelCode(deviceAny.model || deviceAny.model_ref || '');
      setSelectedClientId(device.idClient || '');
      setEditedPhone(device.phone || '');

      // Load client name
      if (device.idClient) {
        clientService.getClientName(device.idClient)
          .then(name => setClientName(name))
          .catch(err => {
            console.error('Failed to load client name:', err);
            setClientName(device.idClient); // Fallback to ID
          });
      }
    }
    // Only re-run when the modal opens or the selected device changes. The
    // hook reads other device.* fields but those always change together with
    // idDevice, so tracking the id is sufficient and avoids re-fetching on
    // parent re-renders that recreate the device object reference.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, device?.idDevice]);

  // Load model refs when device type changes
  useEffect(() => {
    if (selectedTypeCode) {
      const hwType = hardwareTypes.find(t => t.code === selectedTypeCode);
      if (hwType) {
        typesService.getModelReferences(hwType.id, false).then(setModelRefs);
        // Load field definitions for the hardware type (used when overridden)
        typesService.getHardwareFieldDefinitions(hwType.id)
          .then(response => setFieldDefinitions(response.items || []));
      } else {
        setModelRefs([]);
        setFieldDefinitions([]);
      }
    } else {
      setModelRefs([]);
      setFieldDefinitions([]);
    }
  }, [selectedTypeCode, hardwareTypes]);

  const handleSave = async () => {
    if (!device) return;

    try {
      setLoading(true);
      setError(null);

      const updateData: DeviceUpdate = {
        idDevice: device.idDevice,
        deviceType: selectedTypeCode || undefined,
        idClient: selectedClientId || undefined,
        phone: editedPhone || undefined,
        // model_ref would need backend support to update
      };

      await managementApi.apiPatchDeviceV2V2ConfDeviceIdPatch({
        deviceId: device.idDevice,
        deviceUpdate: updateData
      });

      const updatedDevice: DeviceCreate = {
        ...device,
        deviceType: selectedTypeCode,
        idClient: selectedClientId,
        phone: editedPhone,
        model: selectedModelCode,
        model_ref: selectedModelCode,
      } as DeviceCreate;

      // Update client name display
      if (selectedClientId) {
        const selectedClient = clients.find(c => c.id === selectedClientId);
        if (selectedClient) {
          setClientName(selectedClient.name);
        }
      }

      setIsEditing(false);

      if (onDeviceUpdated) {
        onDeviceUpdated(updatedDevice);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update device');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveMaxPriority = async () => {
    if (!device) return;
    const val = maxTemplatePriority.trim() === '' ? null : Number(maxTemplatePriority);
    if (val !== null && (isNaN(val) || val < 1 || val > 100)) return;
    try {
      await deviceService.patchDevice(device.idDevice, { max_template_priority: val });
      const detail = await deviceService.getDeviceDetail(device.idDevice);
      setDeviceDetail(detail);
      setSelectedTemplateId(detail.template_id);
      setMaxTemplatePriority(detail.max_template_priority != null ? String(detail.max_template_priority) : '');
    } catch (err) {
      console.error('Failed to update max_template_priority:', err);
    }
  };

  const handleApplyTemplate = async () => {
    if (!selectedTemplateId || !device) return;

    setApplying(true);
    setError(null);
    try {
      const result = await deviceService.applyTemplate(device.idDevice, selectedTemplateId);
      setDeviceDetail(result);
      if (onDeviceUpdated) {
        onDeviceUpdated({
          ...device,
          mqttHost: result.mqtt_host,
          mqttPort: result.mqtt_port,
          syslogHost: result.syslog_host,
          syslogPort: result.syslog_port,
        } as DeviceCreate);
      }
    } catch (err) {
      setError('Failed to apply template');
      console.error('Failed to apply template:', err);
    } finally {
      setApplying(false);
    }
  };

  const handleResetToTemplate = async () => {
    if (!deviceDetail?.template_id || !device) return;

    setResetting(true);
    setError(null);
    try {
      const result = await deviceService.resetToTemplate(device.idDevice);
      setDeviceDetail(result);
      if (onDeviceUpdated) {
        onDeviceUpdated({
          ...device,
          mqttHost: result.mqtt_host,
          mqttPort: result.mqtt_port,
          syslogHost: result.syslog_host,
          syslogPort: result.syslog_port,
        } as DeviceCreate);
      }
    } catch (err) {
      setError('Failed to reset to template');
      console.error('Failed to reset to template:', err);
    } finally {
      setResetting(false);
    }
  };

  const handleOverrideTemplate = () => {
    // Set config_overridden state - this would need backend support to actually store overrides
    // For now, we show the override UI when config_overridden is true
    if (deviceDetail && !deviceDetail.config_overridden && deviceDetail.template_id) {
      // Create a simulated override state for UI purposes
      setDeviceDetail({
        ...deviceDetail,
        config_overridden: true
      });
    }
  };

  // Format firmware versions for display
  const formatFirmwareVersions = useCallback(() => {
    if (firmwareVersions.length === 0) return null;

    return firmwareVersions
      .map(fw => `${fw.component_type.toLowerCase()} ${fw.version}`)
      .join('; ');
  }, [firmwareVersions]);

  const currentTemplate = templates.find(t => t.id === deviceDetail?.template_id);
  const hasChangedTemplate = selectedTemplateId !== deviceDetail?.template_id;

  // Get effective config values for display
  const getEffectiveConfigValue = (fieldName: string): string => {
    if (!deviceDetail?.effective_config) return '-';
    const value = deviceDetail.effective_config[fieldName];
    if (value === null || value === undefined) return '-';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  // Initialize settings form from categorized device fields
  const initSettingsForm = useCallback(() => {
    if (categorizedFields.length > 0) {
      const editableFields = getEditableFields(categorizedFields);
      setSettingsForm(editableFields);
    }
    // Also snapshot the raw device columns so the legacy column form
    // starts with the current device row values. These only get
    // rendered if the user ticks "Show legacy device columns".
    if (deviceDetail) {
      setLegacyColumnForm({
        syslog_host: deviceDetail.syslog_host ?? '',
        syslog_port: deviceDetail.syslog_port ?? 514,
        syslog_facility: deviceDetail.syslog_facility ?? 'local0',
        provisioning: deviceDetail.provisioning ?? '',
        keepalive_timer: deviceDetail.keepalive_timer ?? 60,
        password: deviceDetail.password ?? '',
        mqtt_host: deviceDetail.mqtt_host ?? '',
        mqtt_port: deviceDetail.mqtt_port ?? 1883,
        mqtt_user: deviceDetail.mqtt_user ?? '',
        mqtt_pass: deviceDetail.mqtt_pass ?? '',
      });
    }
  }, [categorizedFields, deviceDetail]);

  // The full set of editable device columns on the device row. This is
  // the universe from which we filter to get "legacy" columns (those
  // not in the hardware type's schema).
  const ALL_DEVICE_COLUMN_FIELDS = React.useMemo(
    () => [
      { name: 'mqtt_host',       label: 'MQTT Host',        type: 'text' as const },
      { name: 'mqtt_port',       label: 'MQTT Port',        type: 'number' as const },
      { name: 'mqtt_user',       label: 'MQTT User',        type: 'text' as const },
      { name: 'mqtt_pass',       label: 'MQTT Password',    type: 'password' as const },
      { name: 'syslog_host',     label: 'Syslog Host',      type: 'text' as const },
      { name: 'syslog_port',     label: 'Syslog Port',      type: 'number' as const },
      { name: 'syslog_facility', label: 'Syslog Facility',  type: 'text' as const },
      { name: 'provisioning',    label: 'Provisioning URL', type: 'text' as const },
      { name: 'keepalive_timer', label: 'Keepalive Timer',  type: 'number' as const },
      { name: 'password',        label: 'Device Password',  type: 'password' as const },
    ],
    [],
  );

  // Legacy columns = device-row columns that are NOT declared on the
  // current hardware type's schema. Surfaces vestigial values the
  // schema doesn't cover so the user can still edit them if needed.
  const legacyColumns = React.useMemo(() => {
    const schemaNames = new Set(fieldDefinitions.map((d) => d.field_name));
    return ALL_DEVICE_COLUMN_FIELDS.filter((f) => !schemaNames.has(f.name));
  }, [fieldDefinitions, ALL_DEVICE_COLUMN_FIELDS]);

  // Categorize device fields into groups using the hardware-type SCHEMA
  // as the source of truth. Only fields actually declared on the selected
  // hardware type's field_definitions are displayed — everything else
  // (vestigial syslog / mqtt / provisioning columns for hardware types
  // that don't declare them) is excluded.
  //
  // Values are read from `effective_config` so the Settings tab always
  // reflects the assembled result of the backend config-assembly
  // pipeline (hardware defaults → device columns → templates → overrides).
  useEffect(() => {
    if (deviceDetail && fieldDefinitions.length > 0) {
      const groups = categorizeDeviceFieldsFromSchema(deviceDetail, fieldDefinitions);
      setCategorizedFields(groups);
    } else {
      setCategorizedFields([]);
    }
  }, [deviceDetail, fieldDefinitions]);

  // Refresh raw device detail (used by the Raw tab reload button)
  const refreshRawDetail = useCallback(() => {
    if (!device?.idDevice || rawRefreshing) return;
    setRawRefreshing(true);
    deviceService.getDeviceDetail(device.idDevice)
      .then(detail => {
        setDeviceDetail(detail);
        setSelectedTemplateId(detail.template_id);
        setMaxTemplatePriority(detail.max_template_priority != null ? String(detail.max_template_priority) : '');
      })
      .catch(err => console.error('Failed to refresh device detail:', err))
      .finally(() => setRawRefreshing(false));
  }, [device?.idDevice, rawRefreshing]);

  // Fetch v2/conf data when the V2/CONF tab is selected, or when the
  // strict-mode toggle changes. The toggle drives a re-fetch by
  // clearing the cached payload first, so the user sees an instant
  // "loading..." indicator instead of stale JSON.
  useEffect(() => {
    if (activeTab !== 'v2conf' || !device?.idDevice) return;
    if (v2ConfData !== null || v2ConfLoading) return;
    setV2ConfLoading(true);
    const url = v2ConfStrict
      ? `/v2/conf/${device.idDevice}?strict=true`
      : `/v2/conf/${device.idDevice}`;
    apiClient.get(url)
      .then(data => setV2ConfData(data as Record<string, unknown>))
      .catch(err => {
        console.error('Failed to load v2/conf data:', err);
        setV2ConfData(null);
      })
      .finally(() => setV2ConfLoading(false));
  }, [activeTab, device?.idDevice, v2ConfData, v2ConfLoading, v2ConfStrict]);

  const toggleV2ConfStrict = useCallback(() => {
    setV2ConfStrict((prev) => !prev);
    // Drop the cached payload so the fetch effect kicks in again with
    // the new mode.
    setV2ConfData(null);
  }, []);

  // Toggle group expansion
  const toggleGroupExpanded = useCallback((groupCode: string) => {
    setExpandedGroups(prev =>
      prev.includes(groupCode)
        ? prev.filter(code => code !== groupCode)
        : [...prev, groupCode]
    );
  }, []);

  // Check if current values differ from template
  const getTemplateMismatches = useCallback((): string[] => {
    if (!deviceDetail?.template_id || !deviceDetail.effective_config) return [];

    const mismatches: string[] = [];
    const templateConfig = deviceDetail.effective_config;

    // Check each field against template values
    const fieldsToCheck = [
      { key: 'mqtt_host', label: 'MQTT Host', deviceValue: deviceDetail.mqtt_host },
      { key: 'mqtt_port', label: 'MQTT Port', deviceValue: deviceDetail.mqtt_port },
      { key: 'mqtt_user', label: 'MQTT User', deviceValue: deviceDetail.mqtt_user },
      { key: 'syslog_host', label: 'Syslog Host', deviceValue: deviceDetail.syslog_host },
      { key: 'syslog_port', label: 'Syslog Port', deviceValue: deviceDetail.syslog_port },
      { key: 'syslog_facility', label: 'Syslog Facility', deviceValue: deviceDetail.syslog_facility },
      { key: 'keepalive_timer', label: 'Keepalive Timer', deviceValue: deviceDetail.keepalive_timer },
    ];

    for (const field of fieldsToCheck) {
      const templateValue = templateConfig[field.key];
      if (templateValue !== undefined && templateValue !== null && templateValue !== field.deviceValue) {
        mismatches.push(field.label);
      }
    }

    return mismatches;
  }, [deviceDetail]);

  const templateMismatches = getTemplateMismatches();
  const hasTemplateMismatch = templateMismatches.length > 0;

  // Handle settings form changes
  const handleSettingsChange = (field: string, value: string | number) => {
    setSettingsForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Save settings overrides
  const handleSaveSettings = async () => {
    if (!device || !deviceDetail) return;

    setSavingSettings(true);
    setError(null);

    try {
      // Schema-driven path: build override-config payload from changes
      // to the categorized fields (edits go to config_override_json).
      const originalValues = getEditableFields(categorizedFields);
      const overrides: Record<string, unknown> = buildOverrides(originalValues, settingsForm);
      if (customFields.length > 0) {
        overrides.custom_fields = customFields;
      }

      // Legacy column path: build a DeviceUpdate PATCH body from
      // changes to the legacy column form. Only columns NOT declared
      // on the hardware type's schema are ever editable here, so the
      // diff is relative to the original raw column values.
      const legacyPatch: Record<string, unknown> = {};
      if (showLegacyColumns && legacyColumns.length > 0) {
        for (const col of legacyColumns) {
          const current = (deviceDetail as unknown as Record<string, unknown>)[col.name];
          const next = legacyColumnForm[col.name];
          // Normalise numeric comparisons — inputs come in as strings.
          const normalisedNext =
            col.type === 'number' && typeof next === 'string' ? Number(next) : next;
          if (normalisedNext === undefined || normalisedNext === null) continue;
          if (normalisedNext !== current) {
            legacyPatch[col.name] = normalisedNext;
          }
        }
      }

      const hasOverrides = Object.keys(overrides).length > 0;
      const hasLegacyPatch = Object.keys(legacyPatch).length > 0;

      if (!hasOverrides && !hasLegacyPatch) {
        setIsEditingSettings(false);
        return;
      }

      // Apply legacy column patch FIRST, via the existing device
      // PATCH endpoint. This updates the device row columns directly.
      if (hasLegacyPatch) {
        await managementApi.apiPatchDeviceV2V2ConfDeviceIdPatch({
          deviceId: device.idDevice,
          deviceUpdate: {
            idDevice: device.idDevice,
            ...(legacyPatch as Partial<DeviceUpdate>),
          } as DeviceUpdate,
        });
      }

      // Then apply schema-driven overrides (if any) via the override
      // endpoint. The returned DeviceDetail reflects the merged state.
      let updatedDetail = deviceDetail;
      if (hasOverrides) {
        updatedDetail = await deviceService.overrideConfig(device.idDevice, overrides);
      } else if (hasLegacyPatch) {
        // Refetch to pick up the column changes in the Settings display.
        updatedDetail = await deviceService.getDeviceDetail(device.idDevice);
      }

      setDeviceDetail(updatedDetail);
      setIsEditingSettings(false);

      if (onDeviceUpdated) {
        onDeviceUpdated({
          ...device,
          mqttHost: updatedDetail.mqtt_host,
          mqttPort: updatedDetail.mqtt_port,
          syslogHost: updatedDetail.syslog_host,
          syslogPort: updatedDetail.syslog_port,
        } as DeviceCreate);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to save settings');
      console.error('Failed to save settings:', err);
    } finally {
      setSavingSettings(false);
    }
  };

  // Cancel settings edit
  const handleCancelSettingsEdit = () => {
    initSettingsForm();
    setIsEditingSettings(false);
    setShowLegacyColumns(false);
  };

  // Start settings edit
  const handleStartSettingsEdit = () => {
    initSettingsForm();
    setIsEditingSettings(true);
  };

  if (!isOpen || !device) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="device-detail-modal device-detail-modal-v2" data-testid="modal-device-detail" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="modal-header">
          <div className="header-title-section">
            <h2>{device.idDevice}</h2>
            <span className={`status-badge ${device.needsRefresh ? 'needs-refresh' : 'up-to-date'}`}>
              {device.needsRefresh ? t('devices.needsRefresh') : t('common.active')}
            </span>
          </div>
          <div className="header-actions">
            <button className="close-button" data-testid="btn-modal-close" onClick={onClose} aria-label={t('common.close')}>
              ×
            </button>
          </div>
        </div>

        {error && (
          <div className="error-banner">
            <span>{error}</span>
            <button onClick={() => setError(null)}>×</button>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="modal-tabs">
          <button
            className={`tab-button ${activeTab === 'basic' ? 'active' : ''}`}
            data-testid="tab-device-basic"
            onClick={() => setActiveTab('basic')}
          >
            {t('tabs.basic')}
          </button>
          <button
            className={`tab-button ${activeTab === 'settings' ? 'active' : ''}`}
            data-testid="tab-device-settings"
            onClick={() => setActiveTab('settings')}
          >
            {t('tabs.settings')}
          </button>
          {rawTabVisible !== false && (
            <button
              className={`tab-button ${activeTab === 'raw' ? 'active' : ''}`}
              data-testid="tab-device-raw"
              onClick={() => setActiveTab('raw')}
            >
              {t('tabs.raw')}
            </button>
          )}
          <button
            className={`tab-button ${activeTab === 'v2conf' ? 'active' : ''}`}
            data-testid="tab-device-v2conf"
            onClick={() => setActiveTab('v2conf')}
          >
            {t('tabs.v2conf')}
          </button>
          {canViewAuditLog && (
            <button
              className={`tab-button ${activeTab === 'history' ? 'active' : ''}`}
              onClick={() => setActiveTab('history')}
              data-testid="device-history-tab-button"
            >
              {t('tabs.history')}
            </button>
          )}
        </div>

        {/* Basic Tab */}
        {activeTab === 'basic' && (
        <div className="modal-content device-sections">
          {/* Edit button for Basic tab */}
          {!isEditing && (
            <div className="section-actions">
              <button className="btn btn-secondary btn-sm" data-testid="btn-device-edit" onClick={() => setIsEditing(true)}>
                {t('common.edit')}
              </button>
            </div>
          )}

          {/* Section 1: Device Identity */}
          <div className="device-section device-identity-section">
            <h3 className="section-title">{t('devices.deviceIdentity')}</h3>
            <div className="identity-fields">
              <div className="identity-field">
                <label>{t('devices.deviceId')}</label>
                <span className="field-value monospace">{device.idDevice}</span>
              </div>

              <div className="identity-field">
                <label>{t('devices.clientLabel')}</label>
                {isEditing ? (
                  <SearchableSelect
                    value={selectedClientId}
                    onChange={setSelectedClientId}
                    className="field-select"
                    placeholder={t('devices.selectClient')}
                    options={clients.filter(c => c.active !== false).map(client => ({ value: String(client.id), label: client.name }))}
                  />
                ) : (
                  <span className="field-value">
                    {clientName || device.idClient || '-'}
                    {(() => {
                      const assigned = clients.find(c => c.id === device.idClient);
                      return assigned && assigned.active === false ? (
                        <span className="status-badge status-inactive" style={{ marginLeft: 8 }}>Inactive</span>
                      ) : null;
                    })()}
                  </span>
                )}
              </div>

              <div className="identity-field">
                <label>{t('devices.typeLabel')}</label>
                {isEditing ? (
                  <SearchableSelect
                    value={selectedTypeCode}
                    onChange={(next) => {
                      setSelectedTypeCode(next);
                      setSelectedModelCode(''); // Reset model when type changes
                    }}
                    className="field-select"
                    placeholder={t('devices.selectType')}
                    options={hardwareTypes.filter(t => t.is_active).map(type => ({ value: type.code, label: `${type.name} (${type.code})` }))}
                  />
                ) : (
                  <span className="field-value">
                    {hardwareTypes.find(hw => hw.code === (device.deviceType || selectedTypeCode))?.name || device.deviceType || '-'}
                    {(() => {
                      const hwt = hardwareTypes.find(hw => hw.code === (device.deviceType || selectedTypeCode));
                      return hwt && !hwt.is_active ? (
                        <span className="status-badge status-inactive" style={{ marginLeft: 8 }}>Inactive</span>
                      ) : null;
                    })()}
                  </span>
                )}
              </div>

              <div className="identity-field">
                <label>{t('devices.modelLabel')}</label>
                {isEditing ? (
                  <SearchableSelect
                    value={selectedModelCode}
                    onChange={setSelectedModelCode}
                    className="field-select"
                    disabled={!selectedTypeCode || modelRefs.length === 0}
                    placeholder={t('devices.selectModel')}
                    options={modelRefs.filter(r => r.is_active).map(ref => ({ value: ref.code, label: `${ref.code}${ref.name ? ` - ${ref.name}` : ''}` }))}
                  />
                ) : (
                  <span className="field-value">
                    {(device as any).model || (device as any).model_ref || selectedModelCode || '-'}
                  </span>
                )}
              </div>

              <div className="identity-field">
                <label>{t('devices.phone')}</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={editedPhone}
                    onChange={(e) => setEditedPhone(e.target.value.replace(/\D/g, ''))}
                    className="field-input"
                    placeholder={t('devices.phonePlaceholder')}
                    maxLength={15}
                  />
                ) : (
                  <span className="field-value monospace">
                    {device.phone && device.phone !== '000000000000000' ? device.phone : '-'}
                  </span>
                )}
              </div>

            </div>
          </div>

          {/* Section 2: Firmware */}
          <div className="device-section firmware-section">
            <h3 className="section-title">{t('devices.firmware')}</h3>
            <div className="firmware-content">
              {firmwareLoading ? (
                <span className="firmware-loading">{t('devices.firmwareLoading')}</span>
              ) : firmwareVersions.length > 0 ? (
                <div className="firmware-versions">
                  <label>{t('devices.firmwareVersions')}</label>
                  <span className="firmware-list">{formatFirmwareVersions()}</span>
                </div>
              ) : (
                <span className="no-firmware">{t('devices.noFirmwareData')}</span>
              )}
            </div>
          </div>

          {/* Section 3: Configuration Template */}
          <div className="device-section config-section">
            <h3 className="section-title">{t('devices.configurationTemplate')}</h3>

            {/* Template Mismatch Warning */}
            {hasTemplateMismatch && deviceDetail?.template_id && (
              <div className="template-mismatch-warning">
                <div className="warning-icon">⚠</div>
                <div className="warning-content">
                  <strong>{t('devices.valuesDifferFromTemplate')}</strong>
                  <span>{t('devices.templateMismatchWarning', { fields: templateMismatches.join(', ') })}</span>
                </div>
              </div>
            )}

            {deviceDetail?.config_overridden && deviceDetail.template_id ? (
              /* State B: Configuration Overridden */
              <div className="config-overridden">
                <div className="override-header">
                  <span className="override-label">{t('devices.configurationOverridden')}</span>
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={handleResetToTemplate}
                    disabled={resetting || applying}
                  >
                    {resetting ? t('devices.resetting') : t('devices.resetToTemplate')}
                  </button>
                </div>

                {currentTemplate && (
                  <div className="base-template-info">
                    <label>Base template:</label>
                    <span>{currentTemplate.name}</span>
                  </div>
                )}

                <div className="override-fields">
                  {fieldDefinitions.length > 0 ? (
                    fieldDefinitions.map(field => (
                      <div key={field.id} className="override-field">
                        <label>{field.display_name}:</label>
                        <span className="field-value">{getEffectiveConfigValue(field.field_name)}</span>
                      </div>
                    ))
                  ) : (
                    /* Show common fields if no field definitions */
                    <>
                      <div className="override-field">
                        <label>syslog_host:</label>
                        <span className="field-value">{deviceDetail?.syslog_host || '-'}</span>
                      </div>
                      <div className="override-field">
                        <label>syslog_port:</label>
                        <span className="field-value">{deviceDetail?.syslog_port || '-'}</span>
                      </div>
                      <div className="override-field">
                        <label>mqtt_host:</label>
                        <span className="field-value">{deviceDetail?.mqtt_host || '-'}</span>
                      </div>
                      <div className="override-field">
                        <label>mqtt_port:</label>
                        <span className="field-value">{deviceDetail?.mqtt_port || '-'}</span>
                      </div>
                      <div className="override-field">
                        <label>keepalive_timer:</label>
                        <span className="field-value">{deviceDetail?.keepalive_timer || '-'}</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            ) : (
              /* State A: Normal (no override) */
              <div className="config-normal">
                <div className="template-controls">
                  <SearchableSelect
                    value={selectedTemplateId != null ? String(selectedTemplateId) : ''}
                    onChange={(next) => setSelectedTemplateId(next ? Number(next) : null)}
                    disabled={templatesLoading || applying || resetting}
                    className="template-select"
                    placeholder="-- No Template --"
                    options={templates.map((template) => ({ value: String(template.id), label: `${template.name} (${template.device_type}) — priority ${template.priority}` }))}
                  />

                  <button
                    className="btn btn-primary btn-sm"
                    onClick={handleApplyTemplate}
                    disabled={!selectedTemplateId || applying || resetting || (!hasChangedTemplate)}
                  >
                    {applying ? 'Applying...' : hasChangedTemplate ? 'Apply Template' : 'Applied'}
                  </button>

                  {deviceDetail?.template_id && (
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={handleOverrideTemplate}
                      disabled={applying || resetting || deviceDetail.config_overridden}
                    >
                      Override Template
                    </button>
                  )}
                </div>

                <div className="template-priority-cap" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem', fontSize: '0.8rem' }}>
                  <label style={{ color: 'var(--theme-text-secondary, #6b7280)', whiteSpace: 'nowrap' }}>
                    Max template priority:
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    className="cell-input mono"
                    style={{ width: '60px', textAlign: 'center' }}
                    value={maxTemplatePriority}
                    placeholder="none"
                    onChange={(e) => setMaxTemplatePriority(e.target.value.replace(/\D/g, '').slice(0, 3))}
                    disabled={applying || resetting}
                  />
                  <button
                    className="btn btn-secondary btn-sm"
                    style={{ fontSize: '0.75rem', padding: '0.2rem 0.5rem' }}
                    onClick={handleSaveMaxPriority}
                    disabled={applying || resetting ||
                      (maxTemplatePriority === (deviceDetail?.max_template_priority != null ? String(deviceDetail.max_template_priority) : ''))}
                  >
                    Set
                  </button>
                  {deviceDetail?.max_template_priority != null && (
                    <button
                      className="btn btn-secondary btn-sm"
                      style={{ fontSize: '0.75rem', padding: '0.2rem 0.5rem' }}
                      onClick={() => { setMaxTemplatePriority(''); handleSaveMaxPriority(); }}
                      disabled={applying || resetting}
                    >
                      Clear
                    </button>
                  )}
                  <span style={{ color: 'var(--theme-text-tertiary, #9ca3af)', fontSize: '0.75rem' }}>
                    {maxTemplatePriority ? `Templates with priority > ${maxTemplatePriority} excluded` : 'All templates apply'}
                  </span>
                </div>

                {currentTemplate && (
                  <div className="current-template-info">
                    <span className="template-info-label">Current template:</span>
                    <span className="template-info-value">{currentTemplate.name}</span>
                    {currentTemplate.description && (
                      <span className="template-info-desc">{currentTemplate.description}</span>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="modal-content tab-content">
            {/* Settings Header with Edit Button */}
            <div className="settings-header">
              {!isEditingSettings ? (
                <button
                  className="btn btn-secondary btn-sm"
                  data-testid="btn-device-settings-edit"
                  onClick={handleStartSettingsEdit}
                  disabled={loading}
                >
                  Edit Settings
                </button>
              ) : (
                <div className="settings-edit-actions">
                  {legacyColumns.length > 0 && (
                    <label
                      className="legacy-columns-toggle"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 8,
                        fontSize: 13,
                        marginRight: 12,
                        cursor: 'pointer',
                        userSelect: 'none',
                      }}
                      title={
                        'Show device-row columns that are NOT declared on ' +
                        'the hardware type schema. Edits here update the ' +
                        'device row directly (not config_override_json).'
                      }
                    >
                      <input
                        type="checkbox"
                        checked={showLegacyColumns}
                        onChange={(e) => setShowLegacyColumns(e.target.checked)}
                        disabled={savingSettings}
                      />
                      Show legacy device columns
                      <span
                        style={{
                          background: '#e5e7eb',
                          color: '#374151',
                          padding: '1px 6px',
                          borderRadius: 10,
                          fontSize: 11,
                          fontWeight: 600,
                        }}
                      >
                        {legacyColumns.length}
                      </span>
                    </label>
                  )}
                  <button
                    className="btn btn-secondary btn-sm"
                    data-testid="btn-device-settings-cancel"
                    onClick={handleCancelSettingsEdit}
                    disabled={savingSettings}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary btn-sm"
                    data-testid="btn-device-settings-save"
                    onClick={handleSaveSettings}
                    disabled={savingSettings}
                  >
                    {savingSettings ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              )}
            </div>

            {/* Dynamic Field Group Sections — driven by hardware type schema */}
            <div className="field-groups-container">
              {categorizedFields.length === 0 && fieldDefinitions.length === 0 && (
                <p
                  style={{
                    padding: 16,
                    color: '#6b7280',
                    fontSize: 14,
                    textAlign: 'center',
                    background: '#f9fafb',
                    borderRadius: 6,
                  }}
                >
                  No field definitions are configured for hardware type{' '}
                  <strong>{deviceDetail?.deviceType || '—'}</strong>.
                  Define fields via Device Types → Field Definitions to
                  populate this section.
                </p>
              )}
              {categorizedFields
                .filter(group => !group.isSpecial && group.fields.length > 0)
                .map(group => (
                  <FieldGroupSection
                    key={group.code}
                    title={group.name}
                    code={group.code}
                    isExpanded={expandedGroups.includes(group.code)}
                    onToggle={() => toggleGroupExpanded(group.code)}
                    fieldCount={group.fields.length}
                  >
                    <FieldGrid
                      fields={group.fields}
                      isEditing={isEditingSettings}
                      formValues={settingsForm}
                      onChange={handleSettingsChange}
                    />
                  </FieldGroupSection>
                ))}

              {/* Legacy Device Columns — user-toggled editor for raw
                  device row columns that are NOT declared on the hardware
                  type's schema. Edits go directly to the device row via
                  PATCH /v2/conf/{id}, not to config_override_json. */}
              {isEditingSettings && showLegacyColumns && legacyColumns.length > 0 && (
                <FieldGroupSection
                  title={t('devices.legacyDeviceColumns')}
                  code="legacy-columns"
                  isExpanded={expandedGroups.includes('legacy-columns')}
                  onToggle={() => toggleGroupExpanded('legacy-columns')}
                  fieldCount={legacyColumns.length}
                >
                  <div
                    style={{
                      padding: '8px 12px',
                      marginBottom: 12,
                      background: '#fff8e1',
                      border: '1px solid #ffd54f',
                      borderRadius: 6,
                      color: '#6d4c00',
                      fontSize: 12,
                      lineHeight: 1.5,
                    }}
                  >
                    These are raw device row columns that are <strong>not</strong>{' '}
                    declared on the <code>{deviceDetail?.deviceType}</code>{' '}
                    hardware type schema. Edits here update the device row
                    directly via <code>PATCH /v2/conf/{'{id}'}</code>,{' '}
                    <strong>not</strong> via config overrides. They appear
                    in the legacy v1 flat response but are ignored by the
                    schema-driven pipeline on read.
                  </div>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                      gap: 12,
                    }}
                  >
                    {legacyColumns.map((col) => (
                      <div className="form-field" key={col.name}>
                        <label htmlFor={`legacy-${col.name}`}>{col.label}</label>
                        <input
                          id={`legacy-${col.name}`}
                          type={col.type}
                          value={legacyColumnForm[col.name] ?? ''}
                          onChange={(e) =>
                            setLegacyColumnForm((prev) => ({
                              ...prev,
                              [col.name]:
                                col.type === 'number' ? Number(e.target.value) : e.target.value,
                            }))
                          }
                          disabled={savingSettings}
                          style={{
                            width: '100%',
                            padding: '6px 8px',
                            border: '1px solid #d1d5db',
                            borderRadius: 4,
                            fontSize: 13,
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </FieldGroupSection>
              )}

              {/* Additional Fields (merged custom fields + jsonAdditionalConfig) */}
              <FieldGroupSection
                title={t('devices.additionalFieldsSection')}
                code="additional"
                isExpanded={expandedGroups.includes('additional')}
                onToggle={() => toggleGroupExpanded('additional')}
              >
                <AdditionalFieldsEditor
                  customFields={customFields}
                  jsonAdditionalConfig={deviceDetail?.jsonAdditionalConfig || null}
                  onChange={setCustomFields}
                  isEditing={isEditingSettings}
                />
              </FieldGroupSection>

              {/* Template Information (if applicable) */}
              {deviceDetail?.template_id && (
                <FieldGroupSection
                  title={t('devices.templateInformation')}
                  code="template"
                  isExpanded={expandedGroups.includes('template')}
                  onToggle={() => toggleGroupExpanded('template')}
                >
                  <div className="detail-grid">
                    <div className="detail-item">
                      <span className="detail-label">{t('configSources.layers.template')}</span>
                      <span className="detail-value">{deviceDetail.template_name || `ID: ${deviceDetail.template_id}`}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">{t('devices.configOverridden')}</span>
                      <span className={`detail-value badge ${deviceDetail.config_overridden ? 'badge-warning' : 'badge-success'}`}>
                        {deviceDetail.config_overridden ? t('common.yes') : t('common.no')}
                      </span>
                    </div>
                    {deviceDetail.template_applied_at && (
                      <div className="detail-item">
                        <span className="detail-label">{t('devices.appliedAt')}</span>
                        <span className="detail-value">{formatDateTime(deviceDetail.template_applied_at)}</span>
                      </div>
                    )}
                    {deviceDetail.config_override_json && Object.keys(deviceDetail.config_override_json).length > 0 && (
                      <div className="detail-item full-width">
                        <span className="detail-label">{t('devices.overrideJson')}</span>
                        <pre className="json-preview">{JSON.stringify(deviceDetail.config_override_json, null, 2)}</pre>
                      </div>
                    )}
                  </div>
                </FieldGroupSection>
              )}

              {/* Config provenance panel — shows which template/layer produced each value. */}
              {deviceDetail && (
                <ConfigSourcesPanel
                  effectiveConfig={deviceDetail.effective_config}
                  configSources={deviceDetail.config_sources}
                  appliedTemplateIds={deviceDetail.applied_template_ids}
                  lastRequestAt={deviceDetail.last_request_at}
                  lastRequestBy={deviceDetail.last_request_by}
                />
              )}

              {/* Config chain cache panel — shows the cached hash,
                  when it was generated, and recent regeneration
                  audit rows. Renders nothing for devices that have
                  never been polled through the cached assembly
                  path (i.e. all three metadata fields are null). */}
              {deviceDetail && (
                <ConfigChainPanel
                  configHash={deviceDetail.config_hash}
                  configGeneratedAt={deviceDetail.config_generated_at}
                  lastConfigAuditAt={deviceDetail.last_config_audit_at}
                  deviceId={deviceDetail.idDevice}
                />
              )}
            </div>
          </div>
        )}

        {/* Raw Tab — admin-gated via show_raw_tab / raw_tab_usernames. */}
        {activeTab === 'raw' && rawTabVisible !== false && (
          <div className="modal-content tab-content">
            <div className="v2conf-header">
              <span className="v2conf-title">Device Detail (Raw JSON)</span>
              <div className="v2conf-actions">
                {deviceDetail && (
                  <button
                    className={`btn btn-sm btn-secondary v2conf-copy-btn${rawCopied ? ' copied' : ''}`}
                    onClick={() => {
                      navigator.clipboard.writeText(JSON.stringify(deviceDetail, null, 2));
                      setRawCopied(true);
                      setTimeout(() => setRawCopied(false), 2000);
                    }}
                    title={t('devices.copyJsonToClipboard')}
                    type="button"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      {rawCopied ? (
                        <polyline points="20 6 9 17 4 12" />
                      ) : (
                        <>
                          <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                        </>
                      )}
                    </svg>
                    {rawCopied ? 'Copied' : 'Copy'}
                  </button>
                )}
                <button
                  className="btn btn-sm btn-secondary v2conf-refresh-btn"
                  onClick={refreshRawDetail}
                  disabled={rawRefreshing}
                  title={t('devices.refreshDeviceDetail')}
                  type="button"
                >
                  <svg
                    className={`refresh-icon${rawRefreshing ? ' spinning' : ''}`}
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <polyline points="23 4 23 10 17 10" />
                    <polyline points="1 20 1 14 7 14" />
                    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
                  </svg>
                  {rawRefreshing ? 'Refreshing...' : 'Refresh'}
                </button>
              </div>
            </div>
            <div className="raw-json-container">
              <pre className="raw-json">
                {JSON.stringify(deviceDetail, null, 2)}
              </pre>
            </div>
          </div>
        )}

        {/* V2/CONF Tab */}
        {activeTab === 'v2conf' && (
          <div className="modal-content tab-content">
            <div className="v2conf-header">
              <span className="v2conf-title">Device Configuration (v2/conf)</span>
              <div className="v2conf-actions">
                <label
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    fontSize: 12,
                    cursor: 'pointer',
                    userSelect: 'none',
                    marginRight: 8,
                  }}
                  title={
                    'When on, the backend returns only fields declared on the ' +
                    'hardware-type schema + identity metadata. Uses the ' +
                    '?strict=true query parameter — retro-compat preserved.'
                  }
                >
                  <input
                    type="checkbox"
                    checked={v2ConfStrict}
                    onChange={toggleV2ConfStrict}
                    disabled={v2ConfLoading}
                  />
                  Strict mode
                </label>
                {v2ConfData && (
                  <button
                    className={`btn btn-sm btn-secondary v2conf-copy-btn${v2ConfCopied ? ' copied' : ''}`}
                    onClick={() => {
                      navigator.clipboard.writeText(JSON.stringify(v2ConfData, null, 2));
                      setV2ConfCopied(true);
                      setTimeout(() => setV2ConfCopied(false), 2000);
                    }}
                    title={t('devices.copyJsonToClipboard')}
                    type="button"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      {v2ConfCopied ? (
                        <polyline points="20 6 9 17 4 12" />
                      ) : (
                        <>
                          <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                        </>
                      )}
                    </svg>
                    {v2ConfCopied ? 'Copied' : 'Copy'}
                  </button>
                )}
                <button
                  className="btn btn-sm btn-secondary v2conf-refresh-btn"
                  onClick={() => setV2ConfData(null)}
                  disabled={v2ConfLoading}
                  title={t('devices.refreshConfigurationData')}
                  type="button"
                >
                  <svg
                    className={`refresh-icon${v2ConfLoading ? ' spinning' : ''}`}
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <polyline points="23 4 23 10 17 10" />
                    <polyline points="1 20 1 14 7 14" />
                    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
                  </svg>
                  {v2ConfLoading ? 'Refreshing...' : 'Refresh'}
                </button>
              </div>
            </div>
            <div className="raw-json-container">
              {v2ConfLoading && !v2ConfData ? (
                <div className="loading-indicator">
                  Loading V2/CONF data{v2ConfStrict ? ' (strict)' : ''}...
                </div>
              ) : v2ConfData ? (
                <pre className="raw-json">
                  {JSON.stringify(v2ConfData, null, 2)}
                </pre>
              ) : (
                <div className="no-data">{t('devices.noV2ConfData')}</div>
              )}
            </div>
          </div>
        )}

        {/* History Tab — audit-log entries filtered to this device */}
        {activeTab === 'history' && device && (
          <div className="modal-content tab-content">
            <AuditHistoryTab
              resourceType="device"
              resourceId={device.idDevice}
            />
          </div>
        )}

        {/* Modal Footer */}
        <div className="modal-actions">
          {isEditing ? (
            <>
              <div className="action-group-left">
                <button
                  onClick={() => {
                    setIsEditing(false);
                    // Reset to original values
                    setSelectedTypeCode(device.deviceType || '');
                    setSelectedModelCode((device as any).model || (device as any).model_ref || '');
                    setSelectedClientId(device.idClient || '');
                    setEditedPhone(device.phone || '');
                  }}
                  data-testid="btn-device-cancel"
                  className="btn btn-secondary"
                  disabled={loading}
                >
                  Cancel
                </button>
              </div>
              <div className="action-group-right">
                <button
                  onClick={handleSave}
                  data-testid="btn-device-save"
                  className="btn btn-success"
                  disabled={loading}
                >
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </>
          ) : (
            <div className="action-group-right">
              <button onClick={onClose} data-testid="btn-device-close" className="btn btn-secondary" disabled={loading}>
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
